import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NumberField from 'components/library/NumberField';
import { calculatePriceFromMargin, calculateMarginFromPrice } from 'helpers/PurchaseHelper';
import { useTranslation } from 'react-i18next';

export default function MarginEditDialog({
  open,
  handleClose,
  initialMargin,
  cost,
  price,
  itemName,
  onSave,
}) {
  const { t: tCommon } = useTranslation('common');
  const [margin, setMargin] = useState(initialMargin ?? '');
  const [calcPrice, setCalcPrice] = useState(price ?? 0);

  useEffect(() => {
    setMargin(initialMargin !== undefined && initialMargin !== null ? initialMargin : '');
    setCalcPrice(price !== undefined && price !== null ? price : 0);
  }, [open, initialMargin, price]);

  const handleMarginChange = (val) => {
    setMargin(val);
    const marginNum = parseFloat(val);
    const costNum = parseFloat(cost);
    if (!isNaN(costNum) && costNum > 0 && !isNaN(marginNum)) {
      const calculated = calculatePriceFromMargin(costNum, marginNum);
      if (calculated !== null) {
        setCalcPrice(calculated);
      }
    }
  };

  const handlePriceChange = (val) => {
    setCalcPrice(val);
  };

  const calculatedMargin = calculateMarginFromPrice(calcPrice, cost);

  const handleApply = () => {
    onSave(margin, calcPrice);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle style={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" style={{ fontWeight: 600, fontSize: '1.1rem' }}>
          {tCommon('Edit Product Margin')}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers style={{ paddingTop: 16, paddingBottom: 16 }}>
        <Grid container spacing={2}>
          {itemName && (
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                <strong>Item:</strong> {itemName}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary">
              <strong>Cost:</strong> {cost || 0}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary">
              <strong>Selling Price:</strong> {calcPrice || 0}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <NumberField
              label={tCommon('Margin (%)')}
              value={margin}
              onChange={(e) => handleMarginChange(e.target.value)}
              fullWidth
              autoFocus
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <NumberField
              label={tCommon('Selling Price')}
              value={calcPrice}
              onChange={(e) => handlePriceChange(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText={calculatedMargin !== null ? `Calculated Margin: ${calculatedMargin}%` : ''}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: '12px 16px' }}>
        <Button onClick={handleClose} color="inherit">
          {tCommon('Cancel')}
        </Button>
        <Button onClick={handleApply} variant="contained" color="primary">
          {tCommon('Save Margin')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
